import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, Facebook, Instagram, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (response.ok) {
        // Fire Google Ads conversion event
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'ads_conversion_Contact_Us_1', {
            'event_timeout': 2000,
          });
        }
        toast({
          title: "Message Sent!",
          description: "We'll get back to you as soon as possible.",
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        toast({
          title: "Error",
          description: data.detail || "Something went wrong. Please try again.",
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to send message. Please call us at (918) 992-7332.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Phone size={24} />,
      title: 'Phone',
      info: '(918) 992-7332',
      link: 'tel:+19189927332'
    },
    {
      icon: <Mail size={24} />,
      title: 'Email',
      info: 'info@farmdogok.com',
      link: 'mailto:info@farmdogok.com'
    },
    {
      icon: <MapPin size={24} />,
      title: 'Location',
      info: 'Tulsa, Oklahoma',
      link: null
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Contact Farm Dog Oklahoma — Book a Free Consultation | Tulsa OK (918) 992-7332</title>
        <meta name="description" content="Contact Farm Dog Oklahoma for a free dog training consultation. Call (918) 992-7332 or book online. Located in Tulsa, serving Broken Arrow, Owasso, Jenks, Bixby, Sand Springs, Claremore, Sapulpa, and all surrounding Oklahoma cities." />
        <link rel="canonical" href="https://farmdogoklahoma.com/contact" />
        <meta property="og:title" content="Contact Farm Dog Oklahoma — Free Consultation" />
        <meta property="og:description" content="Book a free consultation. Call (918) 992-7332 or contact us online." />
        <meta property="og:url" content="https://farmdogoklahoma.com/contact" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#5C4033] to-[#3E2723] py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#C4A052] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#C4A052] rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Contact Us
            </h1>
            <p className="text-xl md:text-2xl text-[#C4A052] font-semibold">
              Let's Talk About Your Dog's Future
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div variants={fadeInUp}>
              <h2 className="text-4xl font-bold text-[#5C4033] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Send Us a Message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full"
                    placeholder="(918) 123-4567"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full"
                    placeholder="Tell us about your dog and what you're looking for..."
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#C4A052] to-[#B8935E] hover:from-[#B8935E] hover:to-[#C4A052] text-white font-semibold py-6 text-lg rounded-lg transition-all duration-300"
                >
                  {isSubmitting ? 'Sending...' : (
                    <>
                      Send Message <Send className="ml-2" size={20} />
                    </>
                  )}
                </Button>
              </form>
            </motion.div>

            {/* Contact Information */}
            <motion.div variants={fadeInUp} className="space-y-8">
              <div>
                <h2 className="text-4xl font-bold text-[#5C4033] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Get in Touch
                </h2>
                <p className="text-lg text-gray-700 mb-8">
                  Ready to start your dog's training journey? We'd love to hear from you! Reach out to discuss your needs and find the perfect program for your farm dog.
                </p>
              </div>

              {/* Contact Cards */}
              <div className="space-y-4">
                {contactInfo.map((item, index) => (
                  <Card key={index} className="hover-lift border-none shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-[#C4A052] to-[#B8935E] rounded-full text-white flex-shrink-0">
                          {item.icon}
                        </div>
                        <div>
                          <h3 className="font-bold text-[#5C4033] mb-1">{item.title}</h3>
                          {item.link ? (
                            <a href={item.link} className="text-gray-700 hover:text-[#C4A052] transition-colors">
                              {item.info}
                            </a>
                          ) : (
                            <p className="text-gray-700">{item.info}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Hours */}
              <Card className="border-none shadow-lg bg-[#F5F5F0]">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-[#C4A052] to-[#B8935E] rounded-full text-white flex-shrink-0">
                      <Clock size={24} />
                    </div>
                    <h3 className="font-bold text-[#5C4033] text-lg">Hours</h3>
                  </div>
                  <p className="text-gray-700">By Appointment Only</p>
                  <p className="text-sm text-gray-600 mt-2">Contact us to schedule a free consultation</p>
                </CardContent>
              </Card>

              {/* Social Media */}
              <div>
                <h3 className="font-bold text-[#5C4033] mb-4 text-lg">Follow Us</h3>
                <div className="flex gap-4">
                  <a 
                    href="https://www.facebook.com/profile.php?id=100092500893371" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-12 h-12 bg-[#C4A052] rounded-full text-white hover:bg-[#B8935E] transition-colors"
                  >
                    <Facebook size={24} />
                  </a>
                  <a 
                    href="https://www.instagram.com/farmdogok/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-12 h-12 bg-[#C4A052] rounded-full text-white hover:bg-[#B8935E] transition-colors"
                  >
                    <Instagram size={24} />
                  </a>
                </div>
              </div>

              {/* Book Consult Button */}
              <a 
                href="/contact"
                className="block"
              >
                <Button className="w-full bg-[#5C4033] hover:bg-[#3E2723] text-white font-semibold py-6 text-lg rounded-lg transition-all duration-300 hover:shadow-xl">
                  Book Free Consultation
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className="section-padding bg-gradient-to-br from-[#C4A052] to-[#B8935E]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Call today or book online - let's discuss how we can help your dog thrive!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:+19189927332">
              <Button className="bg-white text-[#5C4033] hover:bg-gray-100 font-semibold px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:shadow-xl">
                <Phone className="mr-2" /> Call (918) 992-7332
              </Button>
            </a>
            <a href="mailto:info@farmdogok.com">
              <Button className="bg-[#5C4033] text-white hover:bg-[#3E2723] font-semibold px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:shadow-xl">
                <Mail className="mr-2" /> Email Us
              </Button>
            </a>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default Contact;
